import React from 'react';
import PropTypes from 'prop-types';
import { sortWithDirection } from 'AppSrc/core/utility-functions/sort-functions';
import { ListTable } from './list-table';
import { sortableListTableRowPropShape, listTableColumnPropShape } from './table-proptypes';

class SortableListTable extends React.Component {
  constructor(props) {
    super(props);
    const { initialSortDesc, initialSortedColumn } = this.props;
    this.state = {
      sortedColumn: initialSortedColumn,
      desc: initialSortDesc, // default ascending sort
    };
    this.setSortColumn = this.setSortColumn.bind(this);
    this.compareRows = this.compareRows.bind(this);
  }

  setSortColumn(columnId) {
    this.setState((prevState) => {
      const { sortedColumn, desc } = prevState;
      const currentSortedClicked = columnId === sortedColumn;
      // toggle direction if the same column was clicked
      const newDesc = currentSortedClicked ? !desc : desc;
      // set the clicked column as the sorted column in state
      // and the new value for desc
      return ({
        sortedColumn: columnId,
        desc: newDesc,
      });
    });
  }

  // comparator for sorting rows based
  // on the state value of columnId and desc
  // returns 1, 0 or -1
  compareRows(row1, row2) {
    const { sortedColumn, desc } = this.state;
    // don't sort if there's no defined column
    if (!sortedColumn) { return 0; }
    // get the column definition out of props to determine if we should sort with null last
    const { columns } = this.props;
    const column = columns ? columns.find((col) => col.id === sortedColumn) : null;
    const nullsLast = column ? column.sortWithNullLast : false;
    // row has a dictionary of sort values, by which each column should be sorted
    const value1 = row1.sortValues && row1.sortValues[sortedColumn];
    const value2 = row2.sortValues && row2.sortValues[sortedColumn];
    return sortWithDirection({
      value1,
      value2,
      sortDescending: desc,
      nullsLast,
    });
  }

  // get the rows sorted and limited to the current page
  sortAndPaginateRows() {
    const { rows, rowsPerPage, currentPage } = this.props;
    // first sort the rows
    const sortedRows = [...rows].sort(this.compareRows);

    // if no pagination required, return the full set of rows
    if (!rowsPerPage) {
      return sortedRows;
    }
    // calculate the starting index and return a single page
    const fromIndex = rowsPerPage * (currentPage - 1);
    const toIndex = fromIndex + rowsPerPage;
    return sortedRows.slice(fromIndex, toIndex);
  }

  render() {
    const {
      columns,
      onClickRow,
      className,
      noResultsMessage,
      isDuplicate,
    } = this.props;
    const {
      sortedColumn,
      desc,
    } = this.state;
    const sortedRows = this.sortAndPaginateRows();
    return (
      <ListTable
        className={className}
        columns={columns}
        rows={sortedRows}
        onClickRow={onClickRow}
        setSortColumn={this.setSortColumn}
        sortedColumn={sortedColumn}
        sortDesc={desc}
        noResultsMessage={noResultsMessage}
        isDuplicate={isDuplicate}
      />
    );
  }
}

SortableListTable.propTypes = {
  columns: PropTypes.arrayOf(listTableColumnPropShape),
  rows: PropTypes.arrayOf(sortableListTableRowPropShape),
  onClickRow: PropTypes.func,
  className: PropTypes.string,
  currentPage: PropTypes.number,
  rowsPerPage: PropTypes.number,
  initialSortedColumn: PropTypes.string,
  initialSortDesc: PropTypes.bool,
  noResultsMessage: PropTypes.string,
  isDuplicate: PropTypes.bool,
};

SortableListTable.defaultProps = {
  initialSortDesc: false,
  initialSortedColumn: null,
};

export { SortableListTable };
